import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Labels } from '@core/constants/labels';
import { UserRole } from '@core/enums/user-role.enum';
import { AuthService } from '@core/services/auth-service/auth-service.service';
import { ExamService } from '@core/services/exam-service/exam-service.service';
import { FeatureToggleService } from '@core/services/feature-toggle-service/feature-toggle-service.service';
import { SoundsService } from '@core/services/sounds-service/sounds-service.service';
import { WebsocketService } from '@core/services/websocket-service/websocket-service.service';
import { Subscription } from 'rxjs';
import { User } from 'src/core/models/user.model';
import { ConnectStatusComponent } from '../connect-status/connect-status.component';
import { GradientBoxComponent } from '../gradient-box/gradient-box.component';
import { UserProfileButtonComponent } from '../user-profile-button/user-profile-button.component';
@Component({
  selector: 'app-info-bar',
  templateUrl: './info-bar.component.html',
  styleUrls: ['./info-bar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    GradientBoxComponent,
    MatIconModule,
    MatBadgeModule,
    UserProfileButtonComponent,
    ConnectStatusComponent,
  ],
})
export class InfoBarComponent implements OnInit, OnDestroy {
  labels = Labels.INITIAL_TOOL_PANEL;
  currentSelected = this.labels.MY_EXAMS;
  isAdmin = false;

  @Output() myExamsSelected = new EventEmitter<any>();
  @Output() discussionsSelected = new EventEmitter<any>();
  @Output() databaseSelected = new EventEmitter<any>();
  @Output() registerCase = new EventEmitter<any>();
  @Output() userManagement = new EventEmitter<any>();
  @Output() integrationLog = new EventEmitter<any>();
  @Output() monitorExams = new EventEmitter<any>();

  @ViewChild('menu') menu: any;
  @ViewChild('menuToggle') menuToggle: any;

  subscriptions = new Subscription();
  nonReads = 0;
  showingMenu = false;

  enableDiscussion = false;
  enableRegisterCase = false;
  enableImageDatabase = false;
  enableMonitorCase = false;

  websocketListeners: any[] = [];

  user: User | undefined = undefined;

  isMobile = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private examService: ExamService,
    private websocketService: WebsocketService,
    private soundsService: SoundsService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private featureToggleService: FeatureToggleService,
  ) {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .subscribe((result) => {
        this.isMobile = window.innerWidth < 1000;
        console.log('CHANGED', window.innerWidth);
      });

    this.breakpointObserver
      .observe(['(orientation: portrait)'])
      .subscribe((state) => {
        this.isMobile = window.innerWidth < 1000;
        console.log('CHANGED', window.innerWidth);
      });
    this.featureToggleService.getConfig().subscribe((featureToggle) => {
      this.enableDiscussion = featureToggle.enableDiscussion;
      this.enableRegisterCase = featureToggle.enableRegisterCase;
      this.enableImageDatabase = featureToggle.enableImageDatabase;
      this.enableMonitorCase = featureToggle.enableMonitorCase;
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobile = window.innerWidth < 1000;
  }

  @HostListener('document:click', ['$event'])
  handleAnyClick() {
    if (
      !this.menu?.nativeElement?.contains(event?.target) &&
      !this.menuToggle?.nativeElement?.contains(event?.target) &&
      this.showingMenu
    ) {
      this.showingMenu = false;
    }
  }

  @HostListener('document:touchstart', ['$event'])
  handleAnyTourch() {
    if (
      !this.menu?.nativeElement?.contains(event?.target) &&
      !this.menuToggle?.nativeElement?.contains(event?.target) &&
      this.showingMenu
    ) {
      this.showingMenu = false;
    }
  }

  ngOnInit() {
    this.updateCurrentSelected(this.router.url);
    this.updateData();
    const w1 = this.websocketService.listen('NEW_MESSAGE', (data: any) => {
      this.soundsService.notificationSound();
      this.updateData();
    });
    this.websocketListeners.push(w1);
    this.isAdmin = this.authService.getLoggedUser()?.role === UserRole.ADMIN;
    this.authService.loggedUser.subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });
    this.user = this.authService.getLoggedUser();
  }

  updateCurrentSelected(url: string) {
    if (url.includes('discussion')) {
      this.currentSelected = this.labels.DISCUSSIONS;
    } else if (url.includes('database')) {
      this.currentSelected = this.labels.DATABASE;
    } else if (url.includes('create_exam')) {
      this.currentSelected = this.labels.REGISTER_CASE;
    } else if (url.includes('user_management')) {
      this.currentSelected = this.labels.USER_MANAGEMENT;
    } else if (url.includes('integration_log')) {
      this.currentSelected = this.labels.INTEGRATION_LOG;
    } else if (url.includes('monitor_case')) {
      this.currentSelected = this.labels.MONITOR_EXAMS;
    }
  }

  updateData() {
    const sub = this.examService.getExamsWithDiscussion().subscribe((data) => {
      this.nonReads = data.map((x) => x.non_reads).reduce((x, y) => x + y, 0);
      this.examService.currentNonReads.next(data);
    });
    this.subscriptions.add(sub);
  }

  handleMyExamsSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.MY_EXAMS;
    this.myExamsSelected.emit(null);
  }

  handleDiscussionSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.DISCUSSIONS;
    this.discussionsSelected.emit(null);
  }

  handleDatabaseSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.DATABASE;
    this.databaseSelected.emit(null);
  }

  handleRegisterCaseSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.REGISTER_CASE;
    this.registerCase.emit(null);
  }

  handleUserManagementSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.USER_MANAGEMENT;
    this.userManagement.emit(null);
  }

  handleIntegrationLogSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.INTEGRATION_LOG;
    this.integrationLog.emit(null);
  }

  handleMonitorCaseSelected(): void {
    this.showingMenu = false;
    this.currentSelected = this.labels.MONITOR_EXAMS;
    this.monitorExams.emit(null);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.websocketListeners.forEach((wl) =>
      this.websocketService.stopListening(wl),
    );
  }

  toggleMenu() {
    this.showingMenu = !this.showingMenu;
    console.log('IS SHOWING MENU', this.showingMenu);
  }
}
